<h2 mat-dialog-title class="text-viq-primary">
  {{ mode === 'assign' ? 'Assign Roles From Master' : 'Manage Roles' }}
</h2>

<mat-dialog-content class="manage-org-roles-content">

  <div *ngIf="loading" class="text-center text-muted py-4">Loading roles&hellip;</div>

  <div *ngIf="errorMessage" class="alert alert-danger py-2 my-2" role="alert">{{ errorMessage }}</div>

  <!-- Initial load failed: show a clean error + retry instead of the empty Assign screen. -->
  <div *ngIf="!loading && loadFailed" class="text-center py-3">
    <button type="button" class="btn btn-viq-secondary" (click)="retry()">
      <i class="material-icons align-middle">refresh</i> Retry
    </button>
  </div>

  <!-- First-time setup: pick roles from the master list to seed the organization's catalog. -->
  <ng-container *ngIf="!loading && !loadFailed && mode === 'assign'">
    <p class="text-muted org-roles-hint">
      This organization has no roles yet. Select the roles to copy from the master list. Once saved,
      only these roles are offered on the organization's Case Profiles (you can add or edit them later
      via Manage Roles). The master list itself is not changed.
    </p>
    <div class="org-roles-master-toolbar">
      <label class="org-roles-checkbox">
        <input type="checkbox" [checked]="allMastersSelected"
          (change)="toggleSelectAll($event.target.checked)"> Select all
      </label>
      <span class="org-roles-count">{{ selectedMasterCount }} selected</span>
    </div>
    <div class="org-roles-scroll">
      <table class="table org-roles-table">
        <thead>
          <tr>
            <th class="org-col-assign text-center">Assign</th>
            <th class="org-col-name">Role Name</th>
            <th class="org-col-desc">Description</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let m of masterRoles">
            <td class="text-center">
              <input type="checkbox" [checked]="isMasterSelected(m.id)"
                (change)="toggleMaster(m.id, $event.target.checked)"
                [attr.aria-label]="'Assign ' + m.displayName">
            </td>
            <td>{{ m.displayName }}</td>
            <td class="text-muted">{{ m.description }}</td>
          </tr>
          <tr *ngIf="masterRoles.length === 0">
            <td colspan="3" class="text-center text-muted">No master roles are available.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </ng-container>

  <!-- Ongoing management of the organization's own catalog. -->
  <ng-container *ngIf="!loading && !loadFailed && mode === 'manage'">
    <p class="text-muted org-roles-hint">
      Add, edit, reorder, activate/deactivate or delete this organization's roles. New roles are saved
      only for this organization. Only active roles are offered on the Case Profile Role field.
    </p>
    <div class="org-roles-scroll">
      <table class="table org-roles-table">
        <thead>
          <tr>
            <th class="org-col-name">Role Name</th>
            <th class="org-col-desc">Description</th>
            <th class="org-col-order text-center">Display Order</th>
            <th class="org-col-active text-center">Active</th>
            <th class="org-col-actions text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of roleRows; let i = index" [class.org-role-inactive]="!row.isActive">
            <td>
              <input type="text" class="form-control" [(ngModel)]="row.displayName" maxlength="150"
                placeholder="Select or type a role" [matAutocomplete]="roleAuto" (change)="onManageRoleNameChange(row)"
                [attr.aria-label]="'Role name row ' + (i + 1)">
              <mat-autocomplete #roleAuto="matAutocomplete" (optionSelected)="onManageRoleNameChange(row)">
                <mat-option *ngFor="let m of filteredMasterRoles(row.displayName)" [value]="m.displayName">
                  {{ m.displayName }}
                </mat-option>
              </mat-autocomplete>
            </td>
            <td>
              <input type="text" class="form-control" [(ngModel)]="row.description" maxlength="500"
                placeholder="Description" [attr.aria-label]="'Description row ' + (i + 1)">
            </td>
            <td class="text-center">
              <div class="org-roles-order">
                <input type="number" class="form-control org-roles-order-input"
                  [(ngModel)]="row.displayOrder" min="1" [attr.aria-label]="'Display order row ' + (i + 1)">
                <span class="org-roles-order-arrows">
                  <button type="button" class="org-roles-icon-btn" (click)="moveUp(i)"
                    [disabled]="i === 0" title="Move up" aria-label="Move up">
                    <i class="material-icons">keyboard_arrow_up</i>
                  </button>
                  <button type="button" class="org-roles-icon-btn" (click)="moveDown(i)"
                    [disabled]="i === roleRows.length - 1" title="Move down" aria-label="Move down">
                    <i class="material-icons">keyboard_arrow_down</i>
                  </button>
                </span>
              </div>
            </td>
            <td class="text-center">
              <input type="checkbox" [(ngModel)]="row.isActive"
                [attr.aria-label]="(row.isActive ? 'Deactivate ' : 'Activate ') + (row.displayName || 'role')">
            </td>
            <td class="text-center">
              <button type="button" class="org-roles-icon-btn org-roles-delete" (click)="removeRow(i)"
                title="Delete role" [attr.aria-label]="'Delete ' + (row.displayName || 'role')">
                <i class="material-icons">delete</i>
              </button>
            </td>
          </tr>
          <tr *ngIf="roleRows.length === 0">
            <td colspan="5" class="text-center text-muted">No roles yet. Click "Add New Role".</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="org-roles-add-row">
      <button type="button" class="btn btn-viq-secondary" (click)="addRow()">
        <i class="material-icons align-middle">add</i> Add New Role
      </button>
    </div>
  </ng-container>

</mat-dialog-content>

<mat-dialog-actions align="end" class="org-roles-actions">
  <button class="btn btn-viq-secondary" (click)="cancel()" [disabled]="saving">Cancel</button>
  <button *ngIf="mode === 'assign' && !loadFailed" class="btn btn-viq btn-viq-primary"
    (click)="saveAndFinalize()" [disabled]="saving || loading">Save &amp; Finalize</button>
  <button *ngIf="mode === 'manage' && !loadFailed" class="btn btn-viq btn-viq-primary"
    (click)="saveManage()" [disabled]="saving || loading">Save</button>
</mat-dialog-actions>
